import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { Category, Item, ItemCreate, Orientation, Place, WhereKind } from "@instead/shared";
import { api } from "../lib/api.ts";
import { drafts, itemDraftKey } from "../lib/drafts.ts";
import { moodLabel, useT } from "../lib/i18n.ts";
import { catLabel } from "./DealCard.tsx";
import { Cover } from "./Cover.tsx";
import { CoverMenu, CropCover, dataUrlToBlob, type CoverDraft } from "./CoverEditor.tsx";
import { CatIcon, Ic } from "./Icons.tsx";
import { MarkdownEditor } from "./MarkdownEditor.tsx";
import { NeedChips } from "./NeedChips.tsx";
import { ChipGroup, Dialog, Info, Sheet } from "./Ui.tsx";

/** Everything the form holds; autosaved to the device as a draft (round 3). */
interface Draft {
  title: string; link: string; categoryId: string; durationMin: number | null; customTime: string; openEnded: boolean;
  moods: string[]; orientation: Orientation | null; whereKind: WhereKind; placeId: string | null; needIds: string[];
  tracksProgress: boolean; pinned: boolean; annotation: string; description: string; thumbUrl: string | null;
  cover: CoverDraft;
}

const TIME_SUGGESTIONS = [10, 30, 45, 60];

export interface Prefill { title?: string; link?: string; description?: string; thumbUrl?: string; categoryKind?: Category["kind"] }

function fromItem(item?: Item, prefill?: Prefill): Draft {
  return {
    title: item?.title ?? prefill?.title ?? "",
    link: item?.link ?? prefill?.link ?? "",
    categoryId: item?.categoryId ?? "",
    durationMin: item?.durationMin ?? null,
    customTime: item?.durationMin && !TIME_SUGGESTIONS.includes(item.durationMin) ? String(item.durationMin) : "",
    openEnded: !!item && item.durationMin != null && item.durationMaxMin == null,
    moods: item?.moods ?? [],
    orientation: item?.orientation ?? null,
    whereKind: item?.whereKind ?? "anywhere",
    placeId: item?.placeId ?? null,
    needIds: item?.needIds ?? [],
    tracksProgress: item?.tracksProgress ?? true,
    pinned: item?.pinned ?? false,
    annotation: item?.annotation ?? "",
    description: item?.description ?? prefill?.description ?? "",
    thumbUrl: item?.thumbUrl ?? prefill?.thumbUrl ?? null,
    cover: { kind: "keep" },
  };
}

/**
 * One continuous form for Add and Edit (round 2): title first, always;
 * link/category rows 34px; synced items lock link + length. Categories and
 * places are edited only in Settings — the pickers link there and this
 * draft is waiting when you come back.
 */
export function ItemForm({ item, prefill, onDone }: { item?: Item; prefill?: Prefill; onDone: (saved: Item | null) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const key = itemDraftKey(item?.id ?? "new");
  // A prefill is a new intent (the paste box, the share target): it starts
  // fresh and replaces whatever half-finished "new item" draft was waiting —
  // a stale draft once swallowed a typed title (founder, 2026-09-16, D-42).
  // A bare /add resumes the draft, as round 3 promised.
  const [d, setD] = useState<Draft>(() => {
    const fresh = !!(prefill?.title || prefill?.link);
    if (fresh) {
      const next = fromItem(item, prefill);
      drafts.write(key, next);
      return next;
    }
    return drafts.read<Draft>(key) ?? fromItem(item, prefill);
  });
  const [sheet, setSheet] = useState<"cover" | "category" | "where" | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsInfo, setNeedsInfo] = useState(false);
  const enrichedFor = useRef<string>("");
  const titleRef = useRef<HTMLTextAreaElement>(null);
  // the title wraps instead of clipping (feedback F2, 2026-09-02): grow to fit where field-sizing is missing
  useEffect(() => {
    const el = titleRef.current;
    if (!el || "fieldSizing" in el.style) return;
    el.style.height = "0";
    el.style.height = `${el.scrollHeight}px`;
  }, [d.title]);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const { data: moodsData } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const { data: placesData } = useQuery({ queryKey: ["places"], queryFn: api.places.list, enabled: settings?.features.context ?? false });
  const cats = catData?.categories ?? [];
  const places = placesData?.places ?? [];
  const synced = !!item?.sourceId;

  const set = (patch: Partial<Draft>) => {
    setD((prev) => {
      const next = { ...prev, ...patch };
      drafts.write(key, next);
      return next;
    });
  };

  // Links enrich themselves (D-14): on paste/blur — no button, no claim.
  // A new item with no category yet gets one from the link's host (round 5:
  // defaults over steps) — still a tap away from anything else.
  async function autoEnrich(raw: string) {
    const url = raw.trim();
    if (!/^https?:\/\/\S+\.\S+/.test(url) || enrichedFor.current === url) return;
    enrichedFor.current = url;
    if (!item && !d.categoryId) {
      const guess = cats.find((c) => !c.parentId && c.kind === kindForLink(url));
      if (guess) set({ categoryId: guess.id, tracksProgress: guess.kind !== "activity" });
    }
    try {
      const meta = await api.enrich(url);
      set({
        ...(meta.title && !d.title.trim() ? { title: meta.title } : {}),
        ...(meta.description && !d.description.trim() ? { description: meta.description } : {}),
        ...(meta.thumbUrl ? { thumbUrl: meta.thumbUrl } : {}),
        ...(meta.durationMin && d.durationMin == null && !d.customTime ? (TIME_SUGGESTIONS.includes(meta.durationMin) ? { durationMin: meta.durationMin } : { customTime: String(meta.durationMin), durationMin: null }) : {}),
      });
    } catch {
      // best-effort
    }
  }
  useEffect(() => {
    if (!item && d.link) void autoEnrich(d.link);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (item || d.categoryId || !prefill?.categoryKind || cats.length === 0) return;
    const c = cats.find((x) => !x.parentId && x.kind === prefill.categoryKind);
    if (c) set({ categoryId: c.id, tracksProgress: c.kind !== "activity" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats.length]);

  function pickCategory(id: string) {
    const cat = cats.find((c) => c.id === id);
    const parent = cat?.parentId ? cats.find((c) => c.id === cat.parentId) : undefined;
    set({ categoryId: id, ...(!item ? { tracksProgress: (parent ?? cat)?.kind !== "activity" } : {}) });
    setSheet(null);
  }

  const save = useMutation({
    mutationFn: async (): Promise<Item> => {
      const durationMin = d.durationMin ?? (d.customTime ? parseInt(d.customTime, 10) || null : null);
      const body: ItemCreate = {
        title: d.title.trim(),
        link: d.link.trim() ? d.link.trim() : null,
        description: d.description,
        annotation: d.annotation,
        categoryId: d.categoryId || null,
        durationMin,
        durationMaxMin: durationMin == null ? null : d.openEnded ? null : durationMin,
        moods: d.moods,
        orientation: d.orientation,
        whereKind: d.whereKind,
        placeId: d.whereKind === "place" ? d.placeId : null,
        needIds: d.needIds,
        tracksProgress: d.tracksProgress,
        thumbUrl: d.thumbUrl,
        ...(d.cover.kind === "auto" || d.cover.kind === "none" ? { coverKind: d.cover.kind, coverValue: null } : {}),
        ...(d.cover.kind === "emoji" || d.cover.kind === "text" ? { coverKind: d.cover.kind, coverValue: d.cover.value } : {}),
      };
      let saved = item ? await api.items.update(item.id, { ...body, pinned: d.pinned }) : await api.items.create(body);
      if (d.cover.kind === "image") saved = await api.items.uploadCover(saved.id, await dataUrlToBlob(d.cover.dataUrl));
      if (item && d.pinned !== item.pinned && !("pinned" in body)) saved = await api.items.update(saved.id, { pinned: d.pinned });
      return saved;
    },
    onSuccess: async (saved) => {
      drafts.clear(key);
      await qc.invalidateQueries({ queryKey: ["items"] });
      await qc.invalidateQueries({ queryKey: ["categories"] });
      onDone(saved);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Couldn't save."),
  });

  async function remove() {
    if (!item) return;
    await api.items.remove(item.id);
    drafts.clear(key);
    await qc.invalidateQueries({ queryKey: ["items"] });
    await qc.invalidateQueries({ queryKey: ["categories"] });
    await nav({ to: "/shelf" });
  }

  const goEdit = (to: "/settings/categories" | "/settings/places" | "/settings/moods" | "/settings/needs") => {
    drafts.write(key, d); // the draft waits; Back returns here (round 4)
    setSheet(null);
    void nav({ to, search: { back: item ? `/item/${item.id}?edit=1` : "/add" } as never });
  };

  const cat = cats.find((c) => c.id === d.categoryId);
  const catText = cat ? (cat.parentId ? `${catLabel(cats.find((c) => c.id === cat.parentId), t)} › ${cat.name}` : catLabel(cat, t)) : t("add.choose");
  const place = places.find((p) => p.id === d.placeId);
  const previewItem = {
    title: d.title, coverSeed: item?.coverSeed ?? 7, thumbUrl: d.thumbUrl, coverUrl: item?.coverUrl ?? null,
    coverKind: d.cover.kind === "keep" ? item?.coverKind ?? "auto" : d.cover.kind === "image" ? "image" : d.cover.kind,
    coverValue: d.cover.kind === "emoji" || d.cover.kind === "text" ? d.cover.value : item?.coverValue ?? null,
  } as const;
  const coverPreview = d.cover.kind === "image"
    ? <img className="cover" src={d.cover.dataUrl} alt="" />
    : <Cover item={previewItem} size="l" />;
  const hasCover = d.cover.kind === "keep" ? (item?.coverKind ?? "auto") !== "auto" || !!item?.thumbUrl : d.cover.kind !== "auto";
  const whereOn = settings?.features.context ?? false;
  const needsOn = settings?.features.needs ?? false;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (d.title.trim() && !save.isPending) save.mutate(); }}>
      <div className="screen-head">
        <h1 className="visually-hidden">{t(item ? "add.edit" : "add.title")}</h1>
        <button type="button" className="icon-btn" onClick={() => { drafts.clear(key); onDone(null); }}>{t("cancel")}</button>
        <span style={{ flex: 1 }} />
        <button type="button" className={`icon-btn ${d.pinned ? "accent" : ""}`} aria-pressed={d.pinned} aria-label={t(d.pinned ? "item.pinned" : "item.pin")} title={t(d.pinned ? "item.pinned" : "item.pin")} onClick={() => set({ pinned: !d.pinned })}>
          {d.pinned ? Ic.pinFilled({ size: 16 }) : Ic.pin({ size: 16 })}
        </button>
        <button type="submit" className="btn btn-primary" style={{ minHeight: 36 }} disabled={!d.title.trim() || save.isPending}>{t("save")}</button>
      </div>

      <div className="title-line">
        <textarea ref={titleRef} rows={1} aria-label={t("add.name")} autoFocus={!item} maxLength={300} value={d.title}
          onChange={(e) => set({ title: e.target.value.replace(/[\r\n]+/g, " ") })}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } }} />
        <button type="button" className="icon-btn" style={{ minWidth: 32, minHeight: 32, padding: 0 }} aria-label={t("set.editName")} onClick={() => { titleRef.current?.focus(); titleRef.current?.select(); }}>{Ic.edit({ size: 14 })}</button>
      </div>

      <div>
        <button type="button" className="cover-tap" aria-label={t("add.replace")} title={t("add.replace")} onClick={() => setSheet("cover")}>{coverPreview}</button>
        <div className="cover-actions">
          {d.cover.kind === "image" && <button type="button" className="chip" onClick={() => setSheet("cover")}>{t("add.crop")}</button>}
          {hasCover && <button type="button" className="chip" onClick={() => set({ cover: { kind: "auto" }, thumbUrl: synced ? d.thumbUrl : null })}>{t("add.removeImage")}</button>}
        </div>
        {d.cover.kind === "image" && d.cover.lowRes && (
          <div className="banner" role="status" style={{ marginTop: "var(--sp-2)" }}><span className="dot warn-dot" aria-hidden="true" /><span>{t("cover.lowRes")}</span></div>
        )}
      </div>

      <div className="section">
        <div className="field-row">
          <label htmlFor="f-link">{t("add.link")}</label>
          {synced ? (
            <span className="picker locked" aria-label={`${t("add.link")} · ${t("add.synced")}`}><span className="grow">{d.link}</span>{Ic.lock({ size: 13 })}</span>
          ) : (
            <input id="f-link" className="input" type="url" inputMode="url" value={d.link}
              onChange={(e) => set({ link: e.target.value })}
              onBlur={(e) => void autoEnrich(e.target.value)}
              onPaste={(e) => void autoEnrich(e.clipboardData.getData("text"))} />
          )}
          {synced && <span className="micro">{t("add.synced")}</span>}
        </div>
        <div className="field-row">
          <span className="label" id="f-cat-label">{t("add.category")}</span>
          <button type="button" className="picker" aria-labelledby="f-cat-label" onClick={() => setSheet("category")}>
            <span className="grow">{catText}</span>{Ic.down({ size: 14 })}
          </button>
        </div>
        {synced && item?.durationMin != null && (
          <div className="field-row">
            <span className="label">{t("add.length")}</span>
            <span className="picker locked"><span className="grow">{item.durationMin} {t("item.minutes")}</span>{Ic.lock({ size: 13 })}</span>
            <span className="micro">{t("add.synced")}</span>
          </div>
        )}
      </div>

      {!synced && (
        <div className="section">
          <div className="eyebrow">{t("add.minTime")}</div>
          <div className="chip-row">
            {TIME_SUGGESTIONS.map((m) => (
              <button key={m} type="button" className="chip" aria-pressed={d.durationMin === m} onClick={() => set({ durationMin: d.durationMin === m ? null : m, customTime: "" })}>
                {m === 60 ? "1h" : `${m}m`}
              </button>
            ))}
            <span className="chip" style={{ padding: "0 var(--sp-2)", gap: "var(--sp-1)" }}>
              <label htmlFor="f-custom" className="micro">{t("add.custom")}</label>
              <input id="f-custom" type="number" min={1} max={6000} inputMode="numeric" value={d.customTime}
                onChange={(e) => set({ customTime: e.target.value, durationMin: null })}
                style={{ width: 52, border: "none", background: "transparent", padding: 0, fontSize: "var(--fs-meta)" }} />
            </span>
          </div>
          <label className="check-row" style={{ marginTop: "var(--sp-1)" }}>
            <input type="checkbox" checked={d.openEnded} onChange={(e) => set({ openEnded: e.target.checked })} />
            {t("add.openEnded")}
          </label>
        </div>
      )}

      {(moodsData?.moods.length ?? 0) > 0 && (
        <div className="section">
          <div className="eyebrow">{t("rest.feel")}<EditLink label={t("add.editMoods")} onClick={() => goEdit("/settings/moods")} /></div>
          <ChipGroup multi label={t("rest.feel")} selected={d.moods} options={(moodsData?.moods ?? []).map((m) => ({ value: m.key, label: moodLabel(m) }))} onChange={(moods) => set({ moods })} />
        </div>
      )}

      <div className="section">
        <div className="eyebrow">{t("add.about")}</div>
        <ChipGroup label={t("add.about")} selected={d.orientation ? [d.orientation] : []}
          options={(["create", "consume", "learn", "fun", "be"] as const).map((o) => ({ value: o, label: t(`orient.${o}`) }))}
          onChange={(v) => set({ orientation: (v[0] as Orientation | undefined) ?? null })} />
      </div>

      {whereOn && (
        <div className="section">
          <div className="eyebrow">{t("add.where")}<EditLink label={t("add.editPlaces")} onClick={() => goEdit("/settings/places")} /></div>
          <div className="chip-row" role="group" aria-label={t("add.where")}>
            {(["anywhere", "indoor", "outdoor"] as const).map((k) => (
              <button key={k} type="button" className="chip" aria-pressed={d.whereKind === k} onClick={() => set({ whereKind: k, placeId: null })}>{t(`where.${k}`)}</button>
            ))}
            <button type="button" className="chip" aria-pressed={d.whereKind === "place"} onClick={() => setSheet("where")}>
              {d.whereKind === "place" && place ? place.name : t("where.place")}
            </button>
          </div>
        </div>
      )}

      {needsOn && (
        <div className="section">
          <div className="eyebrow" style={{ flexWrap: "wrap" }}>
            {t("add.needs")}
            <button type="button" className="icon-btn" style={{ minWidth: 28, minHeight: 28, padding: 0 }} aria-label={t("add.needs")} aria-expanded={needsInfo} onClick={() => setNeedsInfo(!needsInfo)}>{Ic.info({ size: 14 })}</button>
            <EditLink label={t("add.editNeeds")} onClick={() => goEdit("/settings/needs")} />
            {needsInfo && <div className="popover" role="note" style={{ flexBasis: "100%", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{t("add.needsInfo")}</div>}
          </div>
          <NeedChips label={t("add.needs")} selected={d.needIds} onChange={(needIds) => set({ needIds })} />
        </div>
      )}

      <div className="section">
        <label className="check-row">
          <input type="checkbox" checked={d.tracksProgress} onChange={(e) => set({ tracksProgress: e.target.checked })} />
          {t("add.trackProgress")}
        </label>
      </div>

      <div className="field section">
        <label htmlFor="f-note">{t("add.annotation")}</label>
        <textarea id="f-note" className="input note" value={d.annotation} onChange={(e) => set({ annotation: e.target.value })} />
      </div>

      <MarkdownEditor id="f-desc" label={t("add.description")} value={d.description} onChange={(description) => set({ description })} />

      {error && <p role="alert" className="meta" style={{ color: "var(--danger)" }}>{error}</p>}

      {item && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--sp-5)" }}>
          <button type="button" className="icon-btn danger" onClick={() => setConfirmDelete(true)}>{Ic.trash({ size: 14 })} {t("item.deleteItem")}</button>
        </div>
      )}

      {sheet === "cover" && !cropFile && (
        <CoverMenu hasLink={!!d.link.trim() || synced} linkThumb={d.thumbUrl} onClose={() => setSheet(null)}
          onPick={(p) => {
            if (p.kind === "file") { setCropFile(p.file); return; }
            set({ cover: p });
            setSheet(null);
          }} />
      )}
      {cropFile && <CropCover file={cropFile} onCancel={() => { setCropFile(null); setSheet(null); }} onUse={(c) => { set({ cover: c }); setCropFile(null); setSheet(null); }} />}

      {sheet === "category" && (
        <Sheet title={t("add.category")} onClose={() => setSheet(null)}
          action={<button type="button" className="icon-btn accent" onClick={() => goEdit("/settings/categories")}>{Ic.edit({ size: 14 })} {t("add.editCategories")}</button>}>
          <button type="button" className="sheet-row" onClick={() => pickCategory("")}><span className="grow">{t("shelf.unsorted")}</span>{!d.categoryId && Ic.check({ size: 16 })}</button>
          {cats.filter((c) => !c.parentId).map((c) => (
            <CategoryRows key={c.id} c={c} cats={cats} selected={d.categoryId} onPick={pickCategory} />
          ))}
        </Sheet>
      )}

      {sheet === "where" && (
        <Sheet title={t("add.where")} onClose={() => setSheet(null)}
          action={<button type="button" className="icon-btn accent" onClick={() => goEdit("/settings/places")}>{Ic.edit({ size: 14 })} {t("add.editPlaces")}</button>}>
          <PlaceRows places={places} selected={d.placeId} onPick={(id) => { set({ whereKind: "place", placeId: id }); setSheet(null); }} />
        </Sheet>
      )}

      {confirmDelete && item && (
        <Dialog title={t("item.deleteQ", { name: item.title })} onClose={() => setConfirmDelete(false)}>
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>{t("keep")}</button>
            <button type="button" className="btn btn-danger" onClick={remove}>{t("delete")}</button>
          </div>
        </Dialog>
      )}
    </form>
  );
}

/** Where a link most likely belongs: video hosts → video, audio hosts → listen, the rest reads. */
function kindForLink(url: string): Category["kind"] {
  let host = "";
  try { host = new URL(url).hostname.replace(/^www\./, ""); } catch { return "article"; }
  if (/(^|\.)(youtube\.com|youtu\.be|vimeo\.com|twitch\.tv|nebula\.tv)$/.test(host)) return "video";
  if (/(^|\.)(spotify\.com|soundcloud\.com|bandcamp\.com|podcasts\.apple\.com|overcast\.fm|pocketcasts\.com)$/.test(host)) return "listen";
  return "article";
}

/** The pencil next to a vocabulary: edit it where it lives; the draft waits.
 *  Shared with the shelf's bulk edit sheet (D-37) so both read the same. */
export function EditLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="icon-btn" style={{ minWidth: 28, minHeight: 28, padding: 0, marginLeft: "var(--sp-1)" }} aria-label={label} title={label} onClick={onClick}>
      {Ic.edit({ size: 13 })}
    </button>
  );
}

/** A top-level category and its children as sheet rows — shared with the source-defaults picker (D-36) and the bulk edit sheet (D-37). */
export function CategoryRows({ c, cats, selected, onPick }: { c: Category; cats: Category[]; selected: string; onPick: (id: string) => void }) {
  const t = useT();
  const kids = cats.filter((k) => k.parentId === c.id);
  return (
    <>
      <button type="button" className="sheet-row" onClick={() => onPick(c.id)}>
        <span style={{ color: `var(--${c.colorToken})`, display: "inline-flex" }}><CatIcon kind={c.kind} size={16} /></span>
        <span className="grow">{catLabel(c, t)}</span>{selected === c.id && Ic.check({ size: 16 })}
      </button>
      {kids.map((k) => (
        <button key={k.id} type="button" className="sheet-row indent" onClick={() => onPick(k.id)}>
          <span className="grow">{k.name}</span>{selected === k.id && Ic.check({ size: 16 })}
        </button>
      ))}
    </>
  );
}

/** The saved places as sheet rows (the item form's and the bulk edit's "A specific place…"). */
export function PlaceRows({ places, selected, onPick }: { places: Place[]; selected: string | null; onPick: (id: string) => void }) {
  const t = useT();
  return (
    <>
      {places.map((p) => (
        <button key={p.id} type="button" className="sheet-row" onClick={() => onPick(p.id)}>
          <span style={{ color: "var(--accent)" }}>{Ic.place({ size: 16 })}</span>
          <span className="grow">{p.name}<span className="meta" style={{ display: "block" }}>{t(p.kind === "indoor" ? "places.indoor" : "places.outdoor")}{p.radiusM ? ` · ${p.radiusM} m` : ""}</span></span>
          {selected === p.id && Ic.check({ size: 16 })}
        </button>
      ))}
      {places.length === 0 && <p className="meta">{t("places.new")}</p>}
    </>
  );
}

export { Link };
